import { useMutation, useQuery, useReactiveVar } from '@apollo/client'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BucketIntegrationMappingDeleteDocument, IntegrationType, UserProfileDocument } from '../../../../../graphql/generated'
import usePendoWhatsAppTracking from '../../../../../hooks/usePendoWhatsAppTracking'
import { WHATSAPP_TRACKING_EVENTS, WHATSAPP_TRACKING_CONFIG } from '../../../../../hooks/whatsAppTrackingConstants'
import { modalVar } from '../../../../../providers/apollo/cache'
import useAlerts from '../../../../buckets/hooks/useAlerts'

export default function DisconnectWhatsAppAccount() {
  const modal = useReactiveVar(modalVar)
  const { createAlert } = useAlerts()
  const { trackWhatsAppEvent } = usePendoWhatsAppTracking()
  const navigate = useNavigate()
  const userProfile = useQuery(UserProfileDocument)
  const whatsappIntegration = userProfile.data?.userProfile?.integrations?.find(
    (integration) => integration?.integrationType === IntegrationType.Whatsapp
  )
  const [searchParams] = useSearchParams()
  const open = modal === 'integrationDisconnectGroupWhatsApp'
  const integrationMappingId = searchParams.get('integrationMappingId')
  const [mutation] = useMutation(BucketIntegrationMappingDeleteDocument)

  const onClose = () => {
    modalVar(null)
    // remove the query parameter because we update the URL to append integrationMappingId for this modal
    navigate('?', { replace: true })
  }
  const onDisconnect = () => {
    trackWhatsAppEvent(WHATSAPP_TRACKING_EVENTS.GROUP_DISCONNECT_CLICKED, {
      integration_mapping_id: integrationMappingId || '',
      integration_id: whatsappIntegration?.id || '',
      source_page: WHATSAPP_TRACKING_CONFIG.SOURCE_PAGES.INTEGRATIONS_MANAGE,
      disconnection_method: WHATSAPP_TRACKING_CONFIG.DISCONNECTION_METHODS.PHOTOBUCKET_UI
    })

    if (!integrationMappingId) {
      createAlert('Integration mapping ID is required to disconnect the WhatsApp group.', 'error')
      onClose()
      return
    }
    if (!whatsappIntegration) {
      createAlert('WhatsApp integration not found.', 'error')
      onClose()
      return
    }

    mutation({
      variables: {
        integrationId: whatsappIntegration.id,
        bucketIntegrationMappingId: integrationMappingId
      },
      onCompleted: () => {
        trackWhatsAppEvent(WHATSAPP_TRACKING_EVENTS.GROUP_DISCONNECTED, {
          integration_mapping_id: integrationMappingId,
          integration_id: whatsappIntegration?.id || '',
          source_page: WHATSAPP_TRACKING_CONFIG.SOURCE_PAGES.INTEGRATIONS_MANAGE,
          disconnection_method: WHATSAPP_TRACKING_CONFIG.DISCONNECTION_METHODS.PHOTOBUCKET_UI
        })
        createAlert('Successfully disconnected the WhatsApp group.', 'success')
        onClose()
      },
      onError: () => {
        createAlert('Failed to disconnect the WhatsApp group.', 'error')
        // Track the error for analytics
        trackWhatsAppEvent(WHATSAPP_TRACKING_EVENTS.GROUP_DISCONNECTED, {
          integration_mapping_id: integrationMappingId || '',
          integration_id: whatsappIntegration?.id || '',
          source_page: WHATSAPP_TRACKING_CONFIG.SOURCE_PAGES.INTEGRATIONS_MANAGE,
          error: WHATSAPP_TRACKING_CONFIG.REASONS.DISCONNECT_FAILED,
          disconnection_method: WHATSAPP_TRACKING_CONFIG.DISCONNECTION_METHODS.PHOTOBUCKET_UI
        })
      }
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" sx={{ borderRadius: '8px', marginLeft: 'auto', marginRight: 'auto' }}>
      <DialogTitle textAlign="center" fontWeight={800} fontSize={16}>
        Disconnect this Group?{' '}
      </DialogTitle>
      <DialogContent>
        <Typography textAlign="center" variant="body1" fontWeight={400} fontSize={16} letterSpacing={0}>
          This action will unlink this Group Chat from the associated Bucket. We will no longer save photos or videos that are shared in the Group
          Chat. Would you like to continue?
        </Typography>
      </DialogContent>
      <Divider sx={{ bgcolor: '#DFDFDF' }} />
      <DialogActions sx={{ paddingTop: '12px', paddingBottom: '12px', justifyContent: 'center' }}>
        <Box display="flex" justifyContent="flex-end" gap={2} width="100%">
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              color: '#1C47CB',
              borderColor: '#1C47CB',
              fontWeight: 600,
              fontSize: '14px',
              borderRadius: '80px',
              textTransform: 'none',
              '&:root': {
                minHeight: 0,
                minWidth: 0
              },
              '&:hover': {
                borderColor: '#1C47CB',
                backgroundColor: '#F5F8FF'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onDisconnect}
            sx={{
              backgroundColor: '#DC362E',
              color: '#fff',
              fontWeight: 600,
              fontSize: '14px',
              borderRadius: '80px',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#b82a23',
                boxShadow: 'none'
              }
            }}
          >
            Disconnect
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
